// useBorefieldData — the legacy Borefield data hook, now a READ-ONLY FACADE over the canonical spine.
//
// Bore→Well cutover Phase 2 (facade-over-spine). In local mode this hook no longer owns a Borefield
// store: it READS Borefield views projected from the spine (pads + wells) via `useBorefieldViews`, and
// its create/update/delete WRITE by migrating the Borefield into the spine (upsert/remove) — the spine
// is the single writable model (epic §6 Sev-1). The public API is unchanged so the ~9 legacy consumers
// are untouched. `useLocalData` (the local-vs-API toggle) still lives in borefieldSlice. RTK Query
// (borefieldApi) remains the "real" (API-mode) code path.

import { useCallback, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { selectUseLocalData } from "../store/borefieldSlice";
import {
  useGetBorefieldsQuery,
  useGetBorefieldQuery,
  useCreateBorefieldMutation,
  useUpdateBorefieldMutation,
  useDeleteBorefieldMutation,
} from "../store/borefieldApi";
import {
  useBorefieldViews,
  upsertBorefieldIntoSpine,
  removeBorefieldFromSpine,
} from "../../wells/store/borefieldFacade";
import { clearPrebakeArtifacts } from "../../prebake/store/prebakeSlice";
// TIME-LOG FOLD (D1): the time log lives per-job (Job.timeLog) in the reactive
// sharded store; delete cleanup drops each job's shard via the store directly.
import { clearJobTimeLog } from "../../timeline/store/jobTimeLogStore";
import type {
  Borefield,
  CreateBorefieldDTO,
  UpdateBorefieldDTO,
} from "../types";
import { generateId, generatePrefixedId } from "../../../lib/id";

interface UseBorefieldDataReturn {
  // Data
  borefields: Borefield[];
  isLoading: boolean;
  error: unknown;

  // Operations
  createBorefield: (data: CreateBorefieldDTO) => Promise<Borefield>;
  updateBorefield: (data: UpdateBorefieldDTO) => Promise<Borefield>;
  deleteBorefield: (id: string) => Promise<void>;
  addBorefield: (borefield: Borefield) => void;

  // Get single borefield
  getBorefieldById: (id: string) => Borefield | undefined;

  // Mode
  isLocalMode: boolean;
}

export function useBorefieldData(): UseBorefieldDataReturn {
  const dispatch = useAppDispatch();
  const isLocalMode = useAppSelector(selectUseLocalData);
  // Local-mode data is projected from the spine (reactive) — the facade, not a second store.
  const localBorefields = useBorefieldViews();

  // API queries (only active when not in local mode)
  const {
    data: apiBorefields,
    isLoading: apiLoading,
    error: apiError,
  } = useGetBorefieldsQuery(undefined, {
    skip: isLocalMode,
  });

  // API mutations
  const [createMutation] = useCreateBorefieldMutation();
  const [updateMutation] = useUpdateBorefieldMutation();
  const [deleteMutation] = useDeleteBorefieldMutation();

  const borefields = useMemo(
    () => (isLocalMode ? localBorefields : (apiBorefields ?? [])),
    [isLocalMode, localBorefields, apiBorefields],
  );
  const isLoading = isLocalMode ? false : apiLoading;
  const error = isLocalMode ? null : apiError;

  // Create borefield
  const createBorefield = useCallback(
    async (data: CreateBorefieldDTO): Promise<Borefield> => {
      if (isLocalMode) {
        const newBorefield: Borefield = {
          ...data,
          id: generatePrefixedId("bf"),
          bores:
            data.bores?.map((b, i) => ({
              ...b,
              id: generateId(),
              boreName: b.boreName || `Bore-${i + 1}`,
              boreType: b.boreType || "Single U-Tube",
            })) ?? [],
          createdAt: new Date().toISOString(),
          modifiedAt: new Date().toISOString(),
          actualBoreCount: data.bores?.length ?? 0,
        };
        // Write-redirect: migrate the whole Borefield into the spine (the single writable model).
        upsertBorefieldIntoSpine(newBorefield);
        return newBorefield;
      } else {
        const result = await createMutation(data).unwrap();
        return result;
      }
    },
    [isLocalMode, createMutation],
  );

  // Update borefield
  const updateBorefield = useCallback(
    async (data: UpdateBorefieldDTO): Promise<Borefield> => {
      if (isLocalMode) {
        const existing = borefields.find((bf) => bf.id === data.id);
        if (!existing) {
          throw new Error(`Borefield ${data.id} not found`);
        }
        const updated: Borefield = {
          ...existing,
          ...data,
          modifiedAt: new Date().toISOString(),
          actualBoreCount: data.bores?.length ?? existing.bores?.length ?? 0,
        };
        upsertBorefieldIntoSpine(updated);
        return updated;
      } else {
        const result = await updateMutation(data).unwrap();
        return result;
      }
    },
    [isLocalMode, borefields, updateMutation],
  );

  // Delete borefield
  const deleteBorefield = useCallback(
    async (id: string): Promise<void> => {
      // Collect this borefield's job ids BEFORE deletion so each job's time-log
      // shard (oxbo-timeline-v8:<jobId>) can be cleaned up — the ids are
      // unrecoverable once the borefield is gone.
      const jobIds = (borefields.find((bf) => bf.id === id)?.bores ?? [])
        .flatMap((b) => b.jobs ?? [])
        .map((j) => j.id);

      if (isLocalMode) {
        removeBorefieldFromSpine(id);
      } else {
        await deleteMutation(id).unwrap();
      }
      // Drop any prebake artifact bundle attached to this borefield so it
      // doesn't orphan in the prebake slice — on BOTH paths (no-op if none was
      // imported; the future API-mode delete must not leak the bundle either).
      dispatch(clearPrebakeArtifacts(id));
      // Same for each job's canonical time-log shard (no-op if none was stored).
      jobIds.forEach(clearJobTimeLog);
    },
    [isLocalMode, borefields, dispatch, deleteMutation],
  );

  // Add a complete borefield directly (for test data / seed / prebake import)
  const addBorefield = useCallback((borefield: Borefield): void => {
    upsertBorefieldIntoSpine(borefield);
  }, []);

  // Get borefield by ID
  const getBorefieldById = useCallback(
    (id: string): Borefield | undefined => {
      return borefields.find((bf) => bf.id === id);
    },
    [borefields],
  );

  return {
    borefields,
    isLoading,
    error,
    createBorefield,
    updateBorefield,
    deleteBorefield,
    addBorefield,
    getBorefieldById,
    isLocalMode,
  };
}

// Hook for single borefield (with API support)
export function useBorefield(id: string | undefined) {
  const isLocalMode = useAppSelector(selectUseLocalData);
  const localBorefields = useBorefieldViews();

  // API query (only active when not in local mode and ID provided)
  const {
    data: apiBorefield,
    isLoading: apiLoading,
    error: apiError,
  } = useGetBorefieldQuery(id ?? "", {
    skip: isLocalMode || !id,
  });

  // Get data from appropriate source
  const borefield = !id
    ? undefined
    : isLocalMode
      ? localBorefields.find((bf) => bf.id === id)
      : apiBorefield;

  const isLoading = isLocalMode ? false : apiLoading;
  const error = isLocalMode ? null : apiError;

  return {
    borefield,
    isLoading,
    error,
    isLocalMode,
  };
}

export default useBorefieldData;
